/**
 * DynamicFieldsSection — renders a grouped section containing every active
 * custom field for a given DocType.
 *
 * Props:
 *   doctype     — one of the allowed DocTypes (passed to useCustomFields).
 *   values      — the current `custom_data` object from the parent form.
 *   onChange    — (fieldname, newValue) callback; parent merges into its state.
 *   title       — optional section header (default: "Custom Fields").
 *
 * Renders nothing when there are no active custom fields for the DocType
 * — keeping forms clean until an admin defines something.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useCustomFields } from '../../hooks/useCustomFields';
import DynamicField from './DynamicField';

const DynamicFieldsSection = ({
  doctype,
  values = {},
  onChange,
  title = 'Custom Fields',
  columns = 2,
}) => {
  const { fields, loading } = useCustomFields(doctype);

  if (loading || !fields.length) return null;

  const handle = (fieldname, v) => {
    if (typeof onChange === 'function') {
      onChange({ ...values, [fieldname]: v });
    }
  };

  return (
    <Card className="mt-4" data-testid={`custom-fields-section-${doctype}`}>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {fields.map((f) => (
          <DynamicField
            key={f.field_id}
            field={f}
            value={values[f.fieldname]}
            onChange={handle}
          />
        ))}
      </CardContent>
    </Card>
  );
};

export default DynamicFieldsSection;
